import React, { useState, useEffect, useRef } from 'react';
import './OptimizedImage.css';

const OptimizedImage = ({ 
  src, 
  alt, 
  title, 
  className, 
  width, 
  height, 
  onError,
  priority = false,
  sizes = "100vw",
  fetchPriority = "auto"
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const imgRef = useRef(null);
  const defaultImage = "https://via.placeholder.com/300x300?text=No+Image";
  
  // Dosya adından SEO dostu bir alt metin oluştur (eğer alt verilmemişse)
  const generateAltFromSrc = (src) => {
    if (!src) return 'Ürün Görseli';
    try {
      // Dosya adını URL'den çıkar
      const fileName = src.split('/').pop().split('.')[0];
      // Alt-tire ve alt çizgileri boşluklara dönüştür
      return fileName.replace(/[-_]/g, ' ');
    } catch (error) {
      return 'Ürün Görseli';
    }
  };

  const handleError = (e) => {
    console.error("Image loading error:", e);
    setHasError(true);
    if (onError) onError(e);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  // Görüntü URL'sini işle ve yükle
  useEffect(() => {
    if (!src) {
      setImgSrc(defaultImage);
      return;
    }

    // Görüntü URL'si değiştiğinde yükleme durumunu sıfırla
    setIsLoaded(false);
    setHasError(false);
    
    // WebP formatını desteklemeyen tarayıcılar için kontrol
    const checkWebpSupport = async () => {
      try {
        // WebP desteğini kontrol et
        const webpSupported = document.createElement('canvas')
          .toDataURL('image/webp')
          .indexOf('data:image/webp') === 0;
        
        // Eğer URL WebP formatındaysa ve tarayıcı desteklemiyorsa
        if (src.toLowerCase().endsWith('.webp') && !webpSupported) {
          // Alternatif bir format kullanabilirsiniz veya proxy üzerinden dönüştürebilirsiniz
          // Şimdilik orijinal URL'yi kullanıyoruz
          console.warn("Browser does not support WebP format, but will try to load anyway");
        }
        
        // CORS hatalarını önlemek için URL'yi kontrol et
        const isCrossDomain = new URL(src, window.location.href).origin !== window.location.origin;
        
        // Görüntüyü önceden yükle
        const img = new Image();
        
        // CORS hatalarını önlemek için
        if (isCrossDomain) {
          img.crossOrigin = "anonymous";
        }
        
        img.onload = () => {
          setImgSrc(src);
          setIsLoaded(true);
        };
        
        img.onerror = (e) => {
          console.error("Error preloading image:", e);
          setHasError(true);
          setImgSrc(defaultImage);
        };
        
        img.src = src;
      } catch (error) {
        console.error("Error checking WebP support:", error);
        setImgSrc(src); // Hata durumunda orijinal URL'yi kullan
      }
    };
    
    checkWebpSupport();
  }, [src, defaultImage]);

  // Intersection Observer ile lazy loading
  useEffect(() => {
    if (!imgRef.current || isLoaded || !imgSrc) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Görüntü viewport'a girdiğinde yükle
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: '200px 0px' } // 200px önce yüklemeye başla
    );
    
    observer.observe(imgRef.current);
    
    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [imgRef, isLoaded, imgSrc]);

  const altText = alt || generateAltFromSrc(src);
  const titleText = title || altText;
  
  // Determine if image should be loaded with priority
  const loadingAttribute = priority ? 'eager' : 'lazy';
  const fetchPriorityAttribute = priority ? 'high' : fetchPriority;

  return (
    <div className="optimized-image-container" style={{ width, height }}>
      {!isLoaded && !hasError && (
        <div className="image-loading-placeholder">
          <div className="logo-loader"></div>
        </div>
      )}
      <img
        ref={imgRef}
        src={imgSrc || (priority ? src : defaultImage)}
        data-src={!priority && !imgSrc ? src : undefined}
        alt={altText}
        title={titleText}
        className={`optimized-image ${className || ''} ${isLoaded ? 'loaded' : 'loading'}`}
        width={width}
        height={height}
        loading={loadingAttribute}
        fetchpriority={fetchPriorityAttribute}
        sizes={sizes}
        onError={handleError}
        onLoad={handleLoad}
        decoding="async"
      />
    </div>
  );
};

export default OptimizedImage; 